import CssBaseline from "@mui/material/CssBaseline";
import React, { useEffect, useState } from "react";
import { connect, useSelector } from "react-redux";
import { Navigate, Outlet } from "react-router-dom";

import { Paths, RequireAuth } from "../common/navigation";

import Button from "@mui/material/Button";
import { makeStyles } from "@mui/styles";
import ErrorDialog from "components/common/ErrorDialog";
import LoadingFullScreen from "components/common/LoadingFullScreen";
import { SMALL_DEVICE_MEDIA_SIZE } from "components/common/constants";
import { FilmReleaseModal } from "components/film";
import { useHasAccessToCurrentPath } from "components/login/useHasAccessToPath";
import { AdvReportMessage } from "components/report/advertising/container";
import { LogReportMessage } from "components/report/logsheet/container";
import { ACTION_STATES } from "core/actions";
import log from "loglevel";
import { ToastContainer } from "react-toastify";
import { isAuthenticatedSelector } from "../login/logic/actions";
import Topbar from "./Topbar";
import RightMenu from "./layout";
import actions from "./logic/actions";
import "./styles.css";

const styles = (theme) => ({
    root: {
        flexGrow: 1,
        overflowX: "hidden",
    },
    rootContainer: {
        [theme.breakpoints.down(SMALL_DEVICE_MEDIA_SIZE)]: {
            marginTop: 70,
            width: "100vw",
        },
    },
});

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
        this.handleOnClose = this.handleOnClose.bind(this);
        this.handleOnReload = this.handleOnReload.bind(this);
    }

    componentDidCatch(error, info) {
        this.setState({
            hasError: true,
            openDialog: true,
            error,
            info,
        });
        log.error("componentDidCatch", error, info);
    }

    handleOnClose() {
        this.setState({ openDialog: false });
    }

    handleOnReload() {
        this.setState({ openDialog: false });
        location.reload();
    }

    getMessage() {
        const { error, info } = this.state;
        if (error) {
            const details = info ? `${info.componentStack}` : error.toString();
            return { message: error.message, details };
        }
        return {
            title: "Unexpected Error",
            message: "Unexpected Error",
        };
    }
    render() {
        const { hasError, openDialog } = this.state;
        const renderAlert = hasError && openDialog;
        const msg = this.getMessage();
        return (
            <React.Fragment>
                {renderAlert && (
                    <ErrorDialog {...msg} defaultOpen={true} onClose={this.handleOnClose} hideClose>
                        <Button onClick={this.handleOnReload} color="secondary">
                            Reload Page
                        </Button>
                    </ErrorDialog>
                )}
                {!renderAlert && this.props.children}
            </React.Fragment>
        );
    }
}
const useStyles = makeStyles(styles);

function MyMain({ onLoadDataApp, loadDataStatus }) {
    const authenticated = useSelector(isAuthenticatedSelector);
    const classes = useStyles();

    const [hasAccess] = useHasAccessToCurrentPath(onRejectAccess);

    const [showAccessDialog, setShowAccessDialog] = useState(!hasAccess);

    useEffect(() => {
        onLoadDataApp();
    }, [onLoadDataApp]);
    function isLoading() {
        return loadDataStatus === ACTION_STATES.NO_REQUEST || loadDataStatus === ACTION_STATES.SEND;
    }

    function handleCloseRedirect() {
        setShowAccessDialog(false);
    }

    function onRejectAccess() {
        setShowAccessDialog(true);
    }

    const loading = isLoading();
    return (
        <React.Fragment>
            <CssBaseline />
            <Topbar>{<RightMenu />}</Topbar>
            <LoadingFullScreen open={loading} />

            <ErrorBoundary>
                {!loading && (
                    <div className={classes.rootContainer}>
                        <FilmReleaseModal />
                        <RequireAuth auth={authenticated}>
                            <Outlet />
                        </RequireAuth>

                        <AdvReportMessage />
                        <LogReportMessage />
                        {!hasAccess && <Navigate to={Paths.ADVERTISING} />}
                        {showAccessDialog && (
                            <ErrorDialog
                                onClose={handleCloseRedirect}
                                defaultOpen
                                maxWidth={"sm"}
                                title={"Access denied"}
                                message={"You do not access to that location"}
                            />
                        )}
                    </div>
                )}
            </ErrorBoundary>

            <ToastContainer
                position="top-center"
                autoClose={5000}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                theme={"colored"}
                pauseOnHover
            />
        </React.Fragment>
    );
}

export default connect(
    ({ globalApp }) => ({
        loadDataStatus: globalApp.loadDataStatus,
    }),
    (dispatch) => ({
        onLoadDataApp: () => dispatch(actions.ON_LOAD_DATA_APP.send()),
    })
)(MyMain);
